import config from '../config.json';

/**
 * Centralized auth token storage utility
 * This ensures we use a consistent approach to storing and retrieving auth tokens
 */

const TOKEN_KEY = config.auth.tokenKey;
const BACKUP_TOKEN_KEY = `${TOKEN_KEY}_backup`;

/**
 * Set the authentication token in storage
 */
export const setAuthToken = (token: string): void => {
  console.log('Setting auth token in storage');
  sessionStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_KEY, token); // Also store in localStorage for better persistence
  // Also save a backup copy for recovery
  sessionStorage.setItem(BACKUP_TOKEN_KEY, token);
};

/**
 * Get the authentication token from storage
 */
export const getAuthToken = (): string | null => {
  // Try getting the token from session storage first
  let token = sessionStorage.getItem(TOKEN_KEY);
  
  // If not found in session storage, try localStorage
  if (!token) {
    token = localStorage.getItem(TOKEN_KEY);
    
    // If found in localStorage but not in sessionStorage, sync them
    if (token) {
      sessionStorage.setItem(TOKEN_KEY, token);
    }
  }
  
  // If still not found, try backup
  if (!token) {
    token = sessionStorage.getItem(BACKUP_TOKEN_KEY);
    if (token) {
      // Restore the token from backup if needed
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_KEY, token);
    }
  }
  
  return token;
};

/**
 * Remove the authentication token from storage
 */
export const removeAuthToken = (): void => {
  console.log('Removing auth token from all storage');
  
  // Remove from both session storage and local storage
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(BACKUP_TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(BACKUP_TOKEN_KEY);
  
  // Log for debugging
  console.log('Auth tokens removed from all storage');
};

/**
 * Check if the authentication token exists
 */
export const hasAuthToken = (): boolean => {
  return !!getAuthToken();
};

/**
 * Initialize auth from session storage (to be called at app startup)
 * Returns true if a token was found and initialized
 */
export const initializeAuth = (): boolean => {
  const token = getAuthToken();
  console.log('Auth initialization - Token in storage:', token ? 'exists' : 'missing');
  
  if (!token) {
    console.log('No token found during initialization');
    return false;
  }
  
  // Validate token format
  console.log('Validating token format during initialization');
  if (!isValidTokenFormat(token)) {
    console.error('Token has invalid format during initialization, removing');
    console.log('Token value (first 10 chars):', token.substring(0, 10) + '...');
    
    // Instead of immediately removing the token, we'll log the error but keep it
    // for troubleshooting purposes - comment this out temporarily
    // removeAuthToken();
    
    return false;
  }
  
  try {
    // This is imported dynamically to avoid circular dependency
    const { api } = require('./api');
    
    // Set the Authorization header
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('Initialization successful - Authorization header set for API calls');
    
    // Debug the header
    console.log('Authorization header:', 
      api.defaults.headers.common['Authorization'] ? 
      'set correctly' : 'FAILED TO SET');
    
    return true;
  } catch (error) {
    console.error('Error during auth initialization:', error);
    return false;
  }
};

// Safe base64 decode helper for JWT tokens
const safeBase64Decode = (str: string): string => {
  // Add padding if needed
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  try {
    return atob(base64);
  } catch (e) {
    console.error('Base64 decode error:', e);
    return '';
  }
};

/**
 * Check if the authentication token has a valid format
 * This doesn't check with the server, just validates basic format
 */
export const isValidTokenFormat = (token: string | null): boolean => {
  if (!token) {
    console.warn('Token validation failed: Token is null or empty');
    return false;
  }
  
  // Check if it's a JWT token (simple format check)
  const parts = token.split('.');
  if (parts.length !== 3) {
    console.warn('Token validation failed: Token does not have valid JWT format (should have 3 parts separated by dots)');
    return false;
  }
  
  try {
    // Try to decode the payload (middle part)
    console.log('Attempting to decode token payload');
    const decodedPayload = safeBase64Decode(parts[1]);
    
    if (!decodedPayload) {
      console.warn('Token validation failed: Could not decode payload');
      return false;
    }
    
    const payload = JSON.parse(decodedPayload);
    
    // Log the actual payload for debugging
    console.log('Token payload:', payload);
    
    // Check if it has an exp property (don't require iat)
    if (!payload.exp) {
      console.warn('Token validation failed: Payload missing expiration date');
      return false;
    }
    
    // Handle exp as either UNIX timestamp or ISO date string
    let expirationTime: number;
    
    if (typeof payload.exp === 'number') {
      // Standard JWT format with UNIX timestamp
      expirationTime = payload.exp;
    } else if (typeof payload.exp === 'string') {
      // If exp is a date string, convert to timestamp
      expirationTime = Math.floor(new Date(payload.exp).getTime() / 1000);
    } else {
      console.warn('Token validation failed: Invalid expiration format');
      return false;
    }
    
    // Check if token is expired - add buffer time (30 seconds) to handle slight clock differences
    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = 30; // 30 seconds buffer
    
    console.log(`Token expiration check: Current time (${currentTime}) vs Token expiration (${expirationTime})`);
    
    if (expirationTime < currentTime - bufferTime) {
      console.warn(`Token validation failed: Token is expired. Expired at ${new Date(expirationTime * 1000)}, current time is ${new Date()}`);
      return false;
    }
    
    console.log('Token validation passed: Token is valid');
    return true;
  } catch (e) {
    console.warn('Token validation failed: Error parsing token payload:', e);
    return false;
  }
};

export default {
  setAuthToken,
  getAuthToken,
  removeAuthToken,
  hasAuthToken,
  initializeAuth,
  isValidTokenFormat
}; 